import React, { useState } from 'react';
import {
  Heart,
  Video,
  Headphones,
  ChefHat,
  FileText,
  ExternalLink
} from 'lucide-react';
import { VitalityItem, VitalityResourceKind } from '../../types/vitality';

const KIND_ICON: Record<VitalityResourceKind, React.ComponentType<{ className?: string }>> = {
  video: Video,
  audio: Headphones,
  recipe: ChefHat,
  article: FileText
};

const GRADIENT_BY_KIND: Record<VitalityResourceKind, string> = {
  video: 'bg-gradient-to-br from-rm-primary-700 via-rm-primary-700/80 to-rm-teal-600',
  audio: 'bg-gradient-to-br from-purple-800 via-purple-700 to-rm-primary-700',
  recipe: 'bg-gradient-to-br from-amber-700 via-amber-600 to-rm-accent-500',
  article: 'bg-gradient-to-br from-rm-primary-700 via-rm-primary-700/90 to-rm-teal-600'
};

const GENERIC_FALLBACK_WHY = new Set([
  'A great starting point.',
  'Goed startpunt.'
]);

const FALLBACK_DESCRIPTION_MAX_LENGTH = 130;

const getDisplayWhy = (why?: string): string => {
  const trimmed = why?.trim() || '';
  return GENERIC_FALLBACK_WHY.has(trimmed) ? '' : trimmed;
};

const normalizeText = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const truncateAtWord = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }
  const truncated = value.slice(0, maxLength).replace(/\s+\S*$/, '').trim();
  return `${truncated || value.slice(0, maxLength).trim()}…`;
};

const getDisplayDescription = (description?: string, title?: string): string => {
  const titleKey = title ? normalizeText(title) : '';
  const lines = (description || '')
    .split(/\r?\n+/)
    .map((line) => line.replace(/^#{1,6}\s*/, '').trim())
    .filter(Boolean)
    .filter((line) => !titleKey || normalizeText(line) !== titleKey);

  const flattened = lines.join(' ').replace(/\s+/g, ' ').trim();
  if (!flattened) {
    return '';
  }

  const firstSentence = flattened.match(/^(.+?[.!?])(?:\s|$)/)?.[1] || flattened;
  return truncateAtWord(firstSentence, FALLBACK_DESCRIPTION_MAX_LENGTH);
};

interface VitalityResourceCardProps {
  item: VitalityItem;
  isFavorited: boolean;
  toggling?: boolean;
  onOpen: (url: string, title: string) => void;
  onToggleFavorite: (item: VitalityItem) => void;
}

export const VitalityResourceCard: React.FC<VitalityResourceCardProps> = ({
  item,
  isFavorited,
  toggling = false,
  onOpen,
  onToggleFavorite
}) => {
  const Icon = KIND_ICON[item.kind] || FileText;
  const gradient = GRADIENT_BY_KIND[item.kind] || GRADIENT_BY_KIND.article;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const hasImage = !!item.thumbnailUrl && !imgError;
  const displayWhy = getDisplayWhy(item.why);
  const displayDescription = getDisplayDescription(item.description, item.title);

  return (
    <div
      className={`relative rounded-card shadow-soft hover:shadow-card transition-shadow overflow-hidden min-h-[280px] ${gradient}`}
    >
      {hasImage && (
        <>
          <img
            src={item.thumbnailUrl as string}
            alt=""
            referrerPolicy="no-referrer"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
          {imgLoaded && <div className="absolute inset-0 bg-black/30" />}
        </>
      )}

      <div className="relative p-4 flex flex-col justify-end min-h-[280px]">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-black/30 backdrop-blur-sm text-white ring-1 ring-white/20">
            <Icon className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <button
              onClick={() => onOpen(item.url, item.title)}
              className="text-left group"
            >
              <h3 className="text-base font-bold transition-colors line-clamp-2 text-white [text-shadow:_0_1px_8px_rgb(0_0_0_/_60%)] group-hover:text-white/90">
                {item.title}
              </h3>
            </button>
            {displayWhy && (
              <p className="text-sm mt-2 line-clamp-6 text-white/90 italic leading-relaxed [text-shadow:_0_1px_4px_rgb(0_0_0_/_60%)]">
                {displayWhy}
              </p>
            )}
            {!displayWhy && displayDescription && (
              <p className="text-sm mt-2 line-clamp-3 text-white/80 leading-relaxed">
                {displayDescription}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/15">
          <button
            onClick={() => onOpen(item.url, item.title)}
            className="text-xs text-white/80 hover:text-white inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            Open
          </button>

          <button
            onClick={() => onToggleFavorite(item)}
            disabled={toggling}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
              isFavorited ? 'text-red-400 hover:text-red-300' : 'text-white/60 hover:text-red-400'
            } ${toggling ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isFavorited ? 'Remove from My Resources' : 'Add to My Resources'}
          >
            {toggling ? (
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Heart className={`w-3.5 h-3.5 ${isFavorited ? 'fill-current' : ''}`} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
